import React, { useRef, useState } from 'react';
import style from './QuantityAdjuster.module.css';

const QuantityAdjuster = ({
    calorRecDtl,
    setCalorRecDtl,
    min = 0,
    max = 9999,
    step = 0.1,
}) => {
    const inputRef = useRef(null);

    // 화면 최초 진입 당시 원본 데이터 보관
    const originalRef = useRef({
        eatAmount: Number(calorRecDtl.eatAmount) || 0,
        servingSize: Number(calorRecDtl.servingSize) || 0,

        // 영양성분 원본
        energy: Number(calorRecDtl.energy) || 0,
        sodium: Number(calorRecDtl.sodium) || 0,
        protein: Number(calorRecDtl.protein) || 0,
        carbohydrate: Number(calorRecDtl.carbohydrate) || 0,
        fat: Number(calorRecDtl.fat) || 0,
    });

    const [selectedType, setSelectedType] = useState('quantity');

    const unitOptions = [
        {
            type: 'quantity',
            label: calorRecDtl.counts,
            valueField: 'eatAmount',
        },
        {
            type: 'capacity',
            label: calorRecDtl.unit,
            valueField: 'servingSize',
        },
    ];

    const selectedOption =
        unitOptions.find((option) => option.type === selectedType) ??
        unitOptions[0];

    const value = calorRecDtl[selectedOption.valueField] ?? '';

    const clamp = (value) =>
        Math.max(min, Math.min(max, value));

    const round = (value) =>
        Math.round(value * 10) / 10;

    const updateValue = (nextValue) => {
        const original = originalRef.current;

        // 현재 선택된 단위의 원본값
        const originalValue =
            Number(original[selectedOption.valueField]) || 0;

        // 변경 비율
        const ratio =
            originalValue === 0
                ? 0
                : Number(nextValue) / originalValue;

        setCalorRecDtl((prev) => ({
            ...prev,

            // 수량, 용량 둘 다 같이 변경
            eatAmount: round(original.eatAmount * ratio),
            servingSize: round(original.servingSize * ratio),

            // 영양성분도 같이 변경
            energy: round(original.energy * ratio),
            sodium: round(original.sodium * ratio),
            protein: round(original.protein * ratio),
            carbohydrate: round(original.carbohydrate * ratio),
            fat: round(original.fat * ratio),
        }));
    };

    const handleInput = (e) => {
        const raw = e.target.value.replace(/[^\d.]/g, '');

        if (raw === '') {
            // 입력 중 빈값 허용
            setCalorRecDtl((prev) => ({
                ...prev,
                [selectedOption.valueField]: '',
            }));
            return;
        }

        const number = Number(raw);

        if (Number.isNaN(number)) {
            return;
        }

        updateValue(clamp(number));
    };

    const handleBlur = () => {
        if (value === '' || Number(value) < min) {
            updateValue(min);
        }
    };

    const handleStep = (delta) => {
        const nextValue =
            clamp((Number(value) || 0) + delta);

        updateValue(
            Math.round(nextValue * 10) / 10
        );
    };

    const handleChangeUnit = (e) => {
        setSelectedType(e.target.value);
    };

    return (
        <div className={style.card}>
            <h3 className={style.title}>용량 조정</h3>

            <div className={style.row}>
                <div className={style.qtyBox}>
                    <button
                        type="button"
                        className={style.stepBtn}
                        onClick={() => handleStep(-step)}
                        disabled={Number(value) <= min}
                        aria-label="수량 감소"
                    >
                        −
                    </button>

                    <input
                        ref={inputRef}
                        type="text"
                        inputMode="decimal"
                        className={style.qtyInput}
                        value={value}
                        onChange={handleInput}
                        onBlur={handleBlur}
                        aria-label="수량"
                    />

                    <button
                        type="button"
                        className={style.stepBtn}
                        onClick={() => handleStep(step)}
                        disabled={Number(value) >= max}
                        aria-label="수량 증가"
                    >
                        +
                    </button>
                </div>

                <div className={style.unitBox}>
                    <select
                        className={style.unitSelect}
                        value={selectedType}
                        onChange={handleChangeUnit}
                        aria-label="단위"
                        style={{ '--unit-len': `${(selectedOption.label ?? '').length}` }}
                    >
                        {unitOptions.map((option) => (
                            <option
                                key={option.type}
                                value={option.type}
                            >
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );
};

export default QuantityAdjuster;